import type { MappingAttribute, ObjectTypeMapping } from '../../types';
import {
  getTextValue,
  normalizeAttributeType,
  unwrapLegacyValue,
} from './pcfSettingsSerialization';

type LegacyBomParameter = {
  Name?: unknown;
  RevitParameter?: unknown;
  POrder?: unknown;
};

function mapIsoAttribute(attribute: any, index: number): MappingAttribute {
  const order = Number(
    unwrapLegacyValue(
      attribute?.cSort ?? attribute?.CSort ?? attribute?.id ?? attribute?.Id ?? index + 1
    )
  );
  const pcfAttributeName = getTextValue(
    unwrapLegacyValue(
      attribute?.pcfName ?? attribute?.PcfName ?? attribute?.name ?? attribute?.Name
    )
  );
  const revitParam = getTextValue(
    unwrapLegacyValue(
      attribute?.parName ??
        attribute?.ParName ??
        attribute?.revitParameter ??
        attribute?.RevitParameter
    )
  );
  const fx = getTextValue(
    unwrapLegacyValue(attribute?.script ?? attribute?.Script ?? attribute?.fx ?? attribute?.Fx)
  );
  const baseType = normalizeAttributeType(
    unwrapLegacyValue(
      attribute?.isoType ??
        attribute?.IsoType ??
        attribute?.attributeType ??
        attribute?.AttributeType
    )
  );

  return {
    order: Number.isFinite(order) && order > 0 ? order : index + 1,
    pcfAttributeName,
    revitParam,
    attributeType: revitParam.trim().toLowerCase() === 'formula' ? 'Formula' : baseType,
    fx,
  };
}

function upsertBomColumns(
  objectTypeMap: Map<string, ObjectTypeMapping>,
  bomParameters: LegacyBomParameter[]
): void {
  if (bomParameters.length === 0) {
    return;
  }

  const allSection = objectTypeMap.get('ALL') ?? { name: 'ALL', attributes: [] };
  const attributes = [...allSection.attributes];

  bomParameters.forEach((parameter) => {
    const pcfAttributeName = getTextValue(parameter.Name).trim();
    const revitParam = getTextValue(parameter.RevitParameter).trim();
    if (!pcfAttributeName || !revitParam) {
      return;
    }

    const existingIndex = attributes.findIndex(
      (attribute) =>
        attribute.pcfAttributeName.trim().toLowerCase() === pcfAttributeName.toLowerCase() ||
        attribute.revitParam.trim().toLowerCase() === revitParam.toLowerCase()
    );
    if (existingIndex >= 0) {
      attributes[existingIndex] = {
        ...attributes[existingIndex],
        attributeType: 'BomColumn',
      };
      return;
    }

    const nextOrder = attributes.reduce(
      (maximum, attribute) => Math.max(maximum, Number(attribute.order) || 0),
      0
    ) + 1;
    const requestedOrder = Number(unwrapLegacyValue(parameter.POrder));
    const requestedOrderAvailable =
      Number.isFinite(requestedOrder) &&
      requestedOrder > 0 &&
      !attributes.some((attribute) => attribute.order === requestedOrder);
    attributes.push({
      order: requestedOrderAvailable ? requestedOrder : nextOrder,
      pcfAttributeName,
      revitParam,
      attributeType: 'BomColumn',
      fx: '',
    });
  });

  objectTypeMap.set('ALL', { ...allSection, attributes });
}

export function buildLegacyPcfObjectTypeMap({
  existingObjectTypes,
  isoMap,
  bomParameters,
}: {
  existingObjectTypes: ObjectTypeMapping[];
  isoMap: Record<string, any[]>;
  bomParameters: LegacyBomParameter[];
}): Map<string, ObjectTypeMapping> {
  const objectTypeMap = new Map(
    existingObjectTypes.map((objectType) => [
      objectType.name,
      { ...objectType, attributes: [...objectType.attributes] },
    ])
  );

  Object.entries(isoMap).forEach(([objectTypeName, attributes]) => {
    const mappedAttributes = (Array.isArray(attributes) ? attributes : [])
      .map(mapIsoAttribute)
      .sort((left, right) => left.order - right.order);
    objectTypeMap.set(objectTypeName, { name: objectTypeName, attributes: mappedAttributes });
  });

  upsertBomColumns(objectTypeMap, bomParameters);
  return objectTypeMap;
}
